import { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  IconButton,
  InputAdornment,
  LinearProgress,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { Add, ArrowLeft2, ArrowRight2, Barcode, Chart, CloseCircle, Edit, Eye, Refresh, Save2, SearchNormal1, Trash } from 'iconsax-reactjs';

import {
  adminApi,
  apiErrorMessage,
  apiValidationErrors,
  bulkDeleteRecords,
  createRecord,
  deleteRecord,
  fetchRecord,
  fetchSelectOptions,
  fetchTable,
  updateRecord,
  type SelectOption
} from '../api/client';
import { can, resourcePermissions } from '../auth/permissions';
import { useAuth } from '../components/AuthGate';
import type { CrudRecord } from '../crud/types';
import { formatDateAuto, t, tLabel } from '../i18n';
import { ItemLabelPreview } from '../features/catalog/ItemLabel';

type Toast = { message: string; severity: 'success' | 'error' | 'warning' };
type ValidationErrors = Record<string, string[]>;

type ItemOption = {
  id: string | number;
  item_id?: string | number;
  name: string;
  code?: string;
  label?: string;
  cost?: number | string;
  variations?: VariationOption[];
};

type VariationOption = {
  id: string | number;
  code?: string;
  sku?: string;
  meta?: unknown;
};

type ComboLine = {
  rowKey: string;
  id?: string | number;
  item_id?: string | number;
  name?: string;
  code?: string;
  cost?: number | string;
  quantity: number;
  variation_id?: string | number | null;
  variations?: VariationOption[];
};

type ChoosableGroup = {
  rowKey: string;
  id?: string | number;
  name: string;
  items: ComboLine[];
};

type ComboPortion = {
  rowKey: string;
  id?: string | number;
  sku: string;
  name: string;
  cost: number | string;
  price: number | string;
  essentials: ComboLine[];
  choosables: ChoosableGroup[];
};

type ComboForm = {
  id?: string | number;
  name: string;
  code: string;
  sku: string;
  symbology: string;
  category_id: string | number | '';
  taxes: Array<string | number>;
  modifiers: Array<string | number>;
  alt_name: string;
  hsn_number: string;
  max_discount: string | number;
  photo?: File | null;
  images?: File[];
  video: string;
  summary: string;
  details: string;
  hide_in_pos: boolean;
  tax_included: boolean;
  portions: ComboPortion[];
};

const symbologyOptions = ['code128', 'ean13', 'upca', 'qrcode'];

function rowKey(prefix: string) {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 8)}`;
}

function makeSku(prefix = 'combo') {
  return `${prefix}-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function emptyLine(): ComboLine {
  return { rowKey: rowKey('line'), quantity: 1, variation_id: null, variations: [] };
}

function emptyPortion(name = 'regular'): ComboPortion {
  return {
    rowKey: rowKey('portion'),
    sku: makeSku('portion'),
    name,
    cost: '',
    price: '',
    essentials: [emptyLine()],
    choosables: []
  };
}

function emptyForm(): ComboForm {
  return {
    name: '',
    code: '',
    sku: makeSku(),
    symbology: 'code128',
    category_id: '',
    taxes: [],
    modifiers: [],
    alt_name: '',
    hsn_number: '',
    max_discount: '',
    photo: null,
    images: [],
    video: '',
    summary: '',
    details: '',
    hide_in_pos: false,
    tax_included: false,
    portions: [emptyPortion()]
  };
}

function relationIds(value: unknown): Array<string | number> {
  if (!Array.isArray(value)) return [];
  return value.flatMap((item) => {
    if (!item || typeof item !== 'object') return [];
    const record = item as Record<string, unknown>;
    const id = record.id ?? record.value;
    return id == null ? [] : [id as string | number];
  });
}

function relationName(value: unknown) {
  if (!value || typeof value !== 'object') return '';
  const record = value as Record<string, unknown>;
  return String(record.name ?? record.label ?? record.title ?? '');
}

function categoryName(row: CrudRecord) {
  if (Array.isArray(row.categories) && row.categories[0]) return relationName(row.categories[0]);
  return relationName(row.category);
}

function moneyValue(value: unknown) {
  const number = Number(value ?? 0);
  return Number.isFinite(number) ? number.toFixed(2) : '0.00';
}

function variationLabel(variation: VariationOption) {
  if (variation.code || variation.sku) return String(variation.code ?? variation.sku);
  if (Array.isArray(variation.meta)) {
    return variation.meta
      .map((entry) => (entry && typeof entry === 'object' ? Object.entries(entry as Record<string, unknown>).map(([k, v]) => `${k}: ${v}`).join(', ') : ''))
      .filter(Boolean)
      .join(', ');
  }
  return String(variation.id);
}

function lineFromRelation(value: unknown): ComboLine {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const item = record.item && typeof record.item === 'object' ? (record.item as Record<string, unknown>) : {};
  const itemId = (record.item_id ?? item.id) as string | number | undefined;
  return {
    rowKey: rowKey('line'),
    id: (record.id ?? itemId) as string | number | undefined,
    item_id: itemId,
    name: String(item.name ?? record.name ?? ''),
    code: String(item.code ?? record.code ?? ''),
    cost: (item.cost ?? record.cost ?? 0) as string | number,
    quantity: Number(record.quantity ?? 1),
    variation_id: (record.variation_id as string | number | null | undefined) ?? null,
    variations: Array.isArray(item.variations) ? (item.variations as VariationOption[]) : []
  };
}

function normalizePortion(value: unknown): ComboPortion {
  const record = value && typeof value === 'object' ? (value as Record<string, unknown>) : {};
  const choosables = Array.isArray(record.choosables)
    ? (record.choosables as Record<string, unknown>[]).map((group) => ({
        rowKey: rowKey('group'),
        id: group.id as string | number | undefined,
        name: String(group.name ?? ''),
        items: Array.isArray(group.items) ? group.items.map(lineFromRelation) : [emptyLine()]
      }))
    : [];

  return {
    rowKey: rowKey('portion'),
    id: record.id as string | number | undefined,
    sku: String(record.sku ?? makeSku('portion')),
    name: String(record.name ?? 'regular'),
    cost: (record.cost ?? '') as string | number,
    price: (record.price ?? '') as string | number,
    essentials: Array.isArray(record.essentials) && record.essentials.length ? record.essentials.map(lineFromRelation) : [emptyLine()],
    choosables
  };
}

function normalizeRecord(record: CrudRecord): ComboForm {
  const categories = relationIds(record.categories);
  const portions = Array.isArray(record.portions) && record.portions.length ? record.portions.map(normalizePortion) : [emptyPortion()];
  return {
    id: record.id,
    name: String(record.name ?? ''),
    code: String(record.code ?? ''),
    sku: String(record.sku ?? makeSku()),
    symbology: String(record.symbology ?? 'code128'),
    category_id: (record.category_id as string | number | undefined) ?? categories[0] ?? '',
    taxes: relationIds(record.taxes),
    modifiers: relationIds(record.modifiers),
    alt_name: String(record.alt_name ?? ''),
    hsn_number: String(record.hsn_number ?? ''),
    max_discount: (record.max_discount ?? '') as string | number,
    photo: null,
    images: [],
    video: String(record.video ?? ''),
    summary: String(record.summary ?? ''),
    details: String(record.details ?? ''),
    hide_in_pos: Boolean(record.hide_in_pos),
    tax_included: Boolean(record.tax_included),
    portions
  };
}

function comboCompositionCount(row: CrudRecord) {
  const portions = Array.isArray(row.portions) ? row.portions : [];
  if (!portions.length) return t('combo.noPortions');
  const essentials = portions.reduce((count, portion) => {
    const record = portion as Record<string, unknown>;
    return count + (Array.isArray(record.essentials) ? record.essentials.length : 0);
  }, 0);
  const choices = portions.reduce((count, portion) => {
    const record = portion as Record<string, unknown>;
    const groups = Array.isArray(record.choosables) ? record.choosables : [];
    return count + groups.reduce((inner, group) => inner + (Array.isArray((group as Record<string, unknown>).items) ? ((group as Record<string, unknown>).items as unknown[]).length : 0), 0);
  }, 0);
  return [t('combo.portionCount', { count: portions.length }), t('combo.essentialCount', { count: essentials }), t('combo.choiceCount', { count: choices })].join(', ');
}

function normalizeLineForSave(line: ComboLine) {
  if (!line.item_id || Number(line.quantity) <= 0) return null;
  return {
    id: line.id ?? line.item_id,
    item_id: line.item_id,
    variation_id: line.variation_id || null,
    quantity: Number(line.quantity)
  };
}

function normalizePayload(form: ComboForm): Record<string, unknown> {
  const portions = form.portions
    .map((portion) => {
      const essentials = portion.essentials.map(normalizeLineForSave).filter(Boolean);
      const choosables = portion.choosables
        .map((group) => ({
          ...(group.id ? { id: group.id } : {}),
          name: group.name,
          items: group.items.map(normalizeLineForSave).filter(Boolean)
        }))
        .filter((group) => group.name.trim() && group.items.length);

      return {
        ...(portion.id ? { id: portion.id } : {}),
        sku: portion.sku || makeSku('portion'),
        name: portion.name || 'regular',
        cost: Number(portion.cost || 0),
        price: Number(portion.price || 0),
        essentials,
        choosables
      };
    })
    .filter((portion) => portion.name && portion.sku && (portion.essentials.length || portion.choosables.length));
  const regular = portions.find((portion) => String(portion.name).toLowerCase() === 'regular') ?? portions[0];

  return {
    // ItemRequest's unique rules (code, sku) exclude the row whose id is in the payload;
    // without it every combo update failed with "code already taken".
    ...(form.id ? { id: form.id } : {}),
    type: 'combo',
    name: form.name,
    code: form.code,
    sku: form.sku,
    symbology: form.symbology || 'code128',
    category_id: form.category_id,
    taxes: form.taxes,
    modifiers: form.modifiers,
    alt_name: form.alt_name,
    hsn_number: form.hsn_number,
    max_discount: form.max_discount,
    video: form.video,
    summary: form.summary,
    details: form.details,
    hide_in_pos: form.hide_in_pos ? 1 : 0,
    tax_included: form.tax_included ? 1 : 0,
    is_stock: 0,
    has_variants: 0,
    has_serials: 0,
    cost: Number(regular?.cost ?? 0),
    price: Number(regular?.price ?? 0),
    stock: [],
    variations: [],
    variants: [],
    portions
  };
}

function appendFormData(formData: FormData, key: string, value: unknown) {
  if (value == null) return;
  if (value instanceof File) {
    formData.append(key, value);
    return;
  }
  if (Array.isArray(value)) {
    value.forEach((entry, index) => appendFormData(formData, `${key}[${index}]`, entry));
    return;
  }
  if (typeof value === 'object') {
    Object.entries(value as Record<string, unknown>).forEach(([childKey, childValue]) => appendFormData(formData, `${key}[${childKey}]`, childValue));
    return;
  }
  formData.append(key, String(value));
}

async function saveComboRecord(editingId: string | number | null, form: ComboForm) {
  const payload = normalizePayload(form);
  const hasFiles = Boolean(form.photo || form.images?.length);
  if (!hasFiles) {
    return editingId ? updateRecord<CrudRecord>('items', editingId, payload) : createRecord<CrudRecord>('items', payload);
  }

  const formData = new FormData();
  Object.entries(payload).forEach(([key, value]) => appendFormData(formData, key, value));
  if (form.photo) formData.append('photo', form.photo);
  form.images?.forEach((image) => formData.append('images[]', image));
  if (editingId) formData.append('_method', 'PUT');
  const response = await adminApi.post<{ success: boolean; data: CrudRecord; message?: string }>(editingId ? `items/${editingId}` : 'items', formData);
  return response.data;
}

function validateGeneral(form: ComboForm) {
  const errors: ValidationErrors = {};
  if (!form.name.trim()) errors.name = [t('validation.nameRequired')];
  if (!form.code.trim()) errors.code = [t('validation.codeRequired')];
  if (!form.symbology.trim()) errors.symbology = [t('validation.symbologyRequired')];
  if (!form.category_id) errors.category_id = [t('validation.categoryRequired')];
  return errors;
}

function validatePortions(form: ComboForm) {
  const errors: ValidationErrors = {};
  const validPortions = form.portions.filter((portion) => {
    const essentialCount = portion.essentials.filter((line) => line.item_id && Number(line.quantity) > 0).length;
    const choosableCount = portion.choosables.reduce((count, group) => count + group.items.filter((line) => line.item_id && Number(line.quantity) > 0).length, 0);
    return portion.name.trim() && portion.sku.trim() && (essentialCount || choosableCount);
  });
  if (!validPortions.length) errors.portions = [t('combo.validation.addItems')];
  validPortions.forEach((portion, index) => {
    if (Number(portion.price || 0) < Number(portion.cost || 0)) errors[`portions.${index}.price`] = [t('combo.validation.priceAboveCost')];
  });
  return errors;
}

function fieldError(errors: ValidationErrors, key: string) {
  return errors[key]?.[0] ?? '';
}

function selectOptionLabel(options: SelectOption[], value: unknown) {
  const option = options.find((item) => item.value === value || String(item.value) === String(value));
  return option?.label ?? '';
}

function yesNo(value: unknown) {
  return Number(value) === 1 ? t('status.yes') : t('status.no');
}

function extrasText(row: CrudRecord) {
  const extras = row.extra_attributes && typeof row.extra_attributes === 'object' && !Array.isArray(row.extra_attributes) ? (row.extra_attributes as Record<string, unknown>) : {};
  const entries = Object.entries(extras).filter(([, value]) => value != null && value !== '');
  return entries.length ? entries.map(([key, value]) => `${key}: ${String(value)}`).join(' · ') : '-';
}

// Legacy ComboListComponent columns.
const comboColumns: Array<{ key: string; label: string; sortable?: boolean; align?: 'right'; render: (row: CrudRecord) => React.ReactNode }> = [
  { key: 'name', label: 'Name', sortable: true, render: (row) => String(row.name ?? '') },
  { key: 'code', label: 'Code', sortable: true, render: (row) => String(row.code ?? '') },
  { key: 'cost', label: 'Cost', sortable: true, align: 'right', render: (row) => moneyValue(row.cost) },
  { key: 'price', label: 'Price', sortable: true, align: 'right', render: (row) => moneyValue(row.price) },
  { key: 'alt_name', label: 'Alternate Name', sortable: true, render: (row) => String(row.alt_name ?? '') || '-' },
  { key: 'categories', label: 'Category', render: (row) => categoryName(row) },
  { key: 'taxes', label: 'Taxes', render: (row) => (Array.isArray(row.taxes) ? row.taxes.map(relationName).filter(Boolean).join(', ') : '') || '-' },
  { key: 'composition', label: 'Portions', render: (row) => comboCompositionCount(row) },
  { key: 'extra_attributes', label: 'Custom fields', render: extrasText },
  { key: 'changeable', label: 'Changeable Price', sortable: true, render: (row) => yesNo(row.changeable) },
  { key: 'min_price', label: 'Minimum Price', sortable: true, align: 'right', render: (row) => (row.min_price == null ? '-' : moneyValue(row.min_price)) },
  { key: 'max_price', label: 'Maximum Price', sortable: true, align: 'right', render: (row) => (row.max_price == null ? '-' : moneyValue(row.max_price)) },
  { key: 'max_discount', label: 'Max Discount', sortable: true, align: 'right', render: (row) => (row.max_discount == null ? '-' : String(Number(row.max_discount))) },
  { key: 'summary', label: 'Summary', sortable: true, render: (row) => String(row.summary ?? '') || '-' },
  { key: 'created_at', label: 'Created At', sortable: true, render: (row) => formatDateAuto(row.created_at) },
  { key: 'updated_at', label: 'Updated At', sortable: true, render: (row) => formatDateAuto(row.updated_at) }
];

export function CombosPage() {
  const { user } = useAuth();
  const permissions = resourcePermissions('items');
  const canRead = can(user, permissions.read);
  const canCreate = can(user, permissions.create);
  const canUpdate = can(user, permissions.update);
  const canDelete = can(user, permissions.delete);

  const [rows, setRows] = useState<CrudRecord[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [limit, setLimit] = useState(10);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [sort, setSort] = useState('code asc');
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [bulkOpen, setBulkOpen] = useState(false);
  const [labelRow, setLabelRow] = useState<CrudRecord | null>(null);
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [editingId, setEditingId] = useState<string | number | null>(null);
  const [form, setForm] = useState<ComboForm>(() => emptyForm());
  const [step, setStep] = useState(0);
  const [formErrors, setFormErrors] = useState<ValidationErrors>({});
  const [saving, setSaving] = useState(false);
  const [toast, setToast] = useState<Toast | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<CrudRecord | null>(null);
  const [options, setOptions] = useState<{ categories: SelectOption[]; taxes: SelectOption[]; modifiers: SelectOption[] }>({
    categories: [],
    taxes: [],
    modifiers: []
  });

  const loadRows = useCallback(async () => {
    if (!canRead) return;
    setLoading(true);
    setError(null);
    try {
      // Server-side search, sort and pagination (legacy ComboListComponent table).
      const response = await fetchTable<CrudRecord>('items', {
        page: page + 1,
        limit,
        query: debouncedQuery || undefined,
        orderBy: sort,
        extra: { type: 'combo' }
      });
      setCount(response.count ?? 0);
      setRows(response.data ?? []);
      setSelected(new Set());
    } catch (err) {
      setError(apiErrorMessage(err, t('combo.loadFailed')));
    } finally {
      setLoading(false);
    }
  }, [canRead, limit, page, debouncedQuery, sort]);

  useEffect(() => {
    void loadRows();
  }, [loadRows]);

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 350);
    return () => window.clearTimeout(timer);
  }, [query]);

  const toggleSort = (key: string) => {
    const [current, dir] = sort.split(' ');
    setSort(current === key && dir === 'asc' ? `${key} desc` : `${key} asc`);
    setPage(0);
  };

  const removeSelected = async () => {
    try {
      const response = await bulkDeleteRecords('items', Array.from(selected));
      setToast({ message: t('crud.bulkDeleted', { count: response.count ?? 0, failed: response.failed ?? 0 }), severity: response.success ? 'success' : 'warning' });
      setBulkOpen(false);
      await loadRows();
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('common.deleteFailed')), severity: 'error' });
    }
  };

  useEffect(() => {
    void Promise.all([
      fetchSelectOptions('all_categories/search').catch(() => []),
      fetchSelectOptions('taxes/search').catch(() => []),
      fetchSelectOptions('modifiers/search').catch(() => [])
    ]).then(([categories, taxes, modifiers]) => setOptions({ categories, taxes, modifiers }));
  }, []);

  const updateForm = <K extends keyof ComboForm>(key: K, value: ComboForm[K]) => {
    setForm((current) => ({ ...current, [key]: value }));
    setFormErrors((current) => {
      if (!current[key as string]) return current;
      const next = { ...current };
      delete next[key as string];
      return next;
    });
  };

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyForm());
    setStep(0);
    setFormErrors({});
    setDialogOpen(true);
  };

  const openEdit = async (row: CrudRecord, openStep = 0) => {
    setEditingId(row.id);
    setStep(openStep);
    setFormErrors({});
    setDialogOpen(true);
    try {
      const record = await fetchRecord<CrudRecord>('items', row.id);
      setForm(normalizeRecord(record));
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('combo.loadOneFailed')), severity: 'error' });
    }
  };

  const openDetail = async (row: CrudRecord) => {
    setDetailOpen(true);
    try {
      const record = await fetchRecord<CrudRecord>('items', row.id);
      setForm(normalizeRecord(record));
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('combo.loadOneFailed')), severity: 'error' });
      setDetailOpen(false);
    }
  };

  const goNext = () => {
    const errors = validateGeneral(form);
    setFormErrors(errors);
    if (!Object.keys(errors).length) setStep(1);
  };

  const save = async (closeAfterSave: boolean) => {
    const errors = { ...validateGeneral(form), ...validatePortions(form) };
    setFormErrors(errors);
    if (Object.keys(errors).length) {
      setStep(errors.portions ? 1 : 0);
      setToast({ message: t('common.fixHighlighted'), severity: 'error' });
      return;
    }

    setSaving(true);
    try {
      const response = await saveComboRecord(editingId, form);
      if (!response.success) {
        setToast({ message: response.message ?? t('combo.saveFailed'), severity: 'error' });
        return;
      }
      const saved = response.data;
      setToast({ message: `${t('combo.singular')} ${editingId ? t('action.updated') : t('action.created')}.`, severity: 'success' });
      setEditingId(saved?.id ?? editingId);
      if (saved?.id) {
        const record = await fetchRecord<CrudRecord>('items', saved.id);
        setForm(normalizeRecord(record));
      }
      if (closeAfterSave) setDialogOpen(false);
      await loadRows();
    } catch (err) {
      const validationErrors = apiValidationErrors(err);
      setFormErrors(validationErrors);
      setToast({ message: Object.keys(validationErrors).length ? t('common.fixHighlighted') : apiErrorMessage(err, t('combo.saveFailed')), severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  const remove = async () => {
    if (!deleteTarget) return;
    try {
      const response = await deleteRecord('items', deleteTarget.id);
      if (!response.success) {
        setToast({ message: response.message ?? t('common.deleteFailed'), severity: 'error' });
        return;
      }
      setDeleteTarget(null);
      setToast({ message: t('common.deleted', { name: t('combo.singular') }), severity: 'success' });
      await loadRows();
    } catch (err) {
      setToast({ message: apiErrorMessage(err, t('common.deleteFailed')), severity: 'error' });
    }
  };

  const pageTitle = editingId ? t('combo.editTitle') : t('combo.addTitle');

  if (!canRead) {
    return <Alert severity="warning">{t('combo.permissionDenied')}</Alert>;
  }

  return (
    <Stack spacing={2}>
      <Paper sx={{ p: 3, borderRadius: 2 }}>
        <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'stretch', md: 'center' }}>
          <Box>
            <Typography variant="h4" fontWeight={700}>{t('combo.title')}</Typography>
            <Typography variant="body2" color="text.secondary">{t('combo.description')}</Typography>
          </Box>
          <Stack direction="row" spacing={1}>
            <TextField
              size="small"
              placeholder={t('action.search')}
              value={query}
              onChange={(event) => {
                setQuery(event.target.value);
                setPage(0);
              }}
              InputProps={{ startAdornment: <InputAdornment position="start"><SearchNormal1 size={16} /></InputAdornment> }}
            />
            <Tooltip title={t('action.refresh')}>
              <IconButton onClick={() => void loadRows()}><Refresh size={18} /></IconButton>
            </Tooltip>
            {canDelete && selected.size ? (
              <Button color="error" variant="outlined" startIcon={<Trash size={16} />} onClick={() => setBulkOpen(true)}>
                {t('action.deleteSelected')} ({selected.size})
              </Button>
            ) : null}
            {canCreate ? (
              <Button variant="contained" startIcon={<Add size={18} />} onClick={openCreate}>{t('combo.addTitle')}</Button>
            ) : null}
          </Stack>
        </Stack>
      </Paper>

      <Paper sx={{ overflow: 'hidden', borderRadius: 2 }}>
        {loading ? <LinearProgress /> : null}
        {error ? <Alert severity="error" sx={{ m: 2 }}>{error}</Alert> : null}
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                {canDelete ? (
                  <TableCell padding="checkbox">
                    <Checkbox
                      size="small"
                      checked={rows.length > 0 && rows.every((row) => selected.has(String(row.id)))}
                      indeterminate={selected.size > 0 && !rows.every((row) => selected.has(String(row.id)))}
                      onChange={(event) => setSelected(event.target.checked ? new Set(rows.map((row) => String(row.id))) : new Set())}
                    />
                  </TableCell>
                ) : null}
                {comboColumns.map((column) => (
                  <TableCell key={column.key} align={column.align}>
                    {column.sortable ? (
                      <TableSortLabel active={sort.split(' ')[0] === column.key} direction={sort.split(' ')[0] === column.key && sort.endsWith('desc') ? 'desc' : 'asc'} onClick={() => toggleSort(column.key)}>
                        {tLabel(column.label)}
                      </TableSortLabel>
                    ) : column.key === 'composition' ? t('combo.itemsStep') : tLabel(column.label)}
                  </TableCell>
                ))}
                <TableCell align="right">{t('action.actions')}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {!loading && !rows.length ? (
                <TableRow>
                  <TableCell colSpan={comboColumns.length + 2}>
                    <Stack alignItems="center" spacing={1.5} sx={{ py: 8 }}>
                      <Typography variant="h6" fontWeight={700}>{t('combo.emptyTitle')}</Typography>
                      <Typography variant="body2" color="text.secondary">{t('combo.emptyDescription')}</Typography>
                    </Stack>
                  </TableCell>
                </TableRow>
              ) : null}
              {rows.map((row) => (
                <TableRow key={String(row.id)} hover selected={selected.has(String(row.id))}>
                  {canDelete ? (
                    <TableCell padding="checkbox">
                      <Checkbox
                        size="small"
                        checked={selected.has(String(row.id))}
                        onChange={(event) => setSelected((current) => {
                          const next = new Set(current);
                          if (event.target.checked) next.add(String(row.id));
                          else next.delete(String(row.id));
                          return next;
                        })}
                      />
                    </TableCell>
                  ) : null}
                  {comboColumns.map((column) => (
                    <TableCell key={column.key} align={column.align} sx={column.key === 'summary' ? { maxWidth: 280, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' } : undefined}>
                      {column.render(row)}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Stack direction="row" spacing={0.5} justifyContent="flex-end">
                      <Tooltip title={t('action.view')}>
                        <IconButton size="small" onClick={() => void openDetail(row)}><Eye size={17} /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('catalog.action.printLabel')}>
                        <IconButton size="small" onClick={() => setLabelRow(row)}><Barcode size={17} /></IconButton>
                      </Tooltip>
                      <Tooltip title={t('catalog.action.trails')}>
                        <IconButton size="small" onClick={() => navigate(`/items/${row.id}/trails`)}><Chart size={17} /></IconButton>
                      </Tooltip>
                      {canUpdate ? (
                        <Tooltip title={t('action.edit')}>
                          <IconButton size="small" color="primary" onClick={() => void openEdit(row, 0)}><Edit size={17} /></IconButton>
                        </Tooltip>
                      ) : null}
                      {canDelete ? (
                        <Tooltip title={t('action.delete')}>
                          <IconButton size="small" color="error" onClick={() => setDeleteTarget(row)}><Trash size={17} /></IconButton>
                        </Tooltip>
                      ) : null}
                    </Stack>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
            <TableFooter>
              <TableRow>
                <TablePagination
                  count={count}
                  page={page}
                  rowsPerPage={limit}
                  rowsPerPageOptions={[10, 25, 50, 100]}
                  onPageChange={(_event, nextPage) => setPage(nextPage)}
                  onRowsPerPageChange={(event) => {
                    setLimit(Number(event.target.value));
                    setPage(0);
                  }}
                />
              </TableRow>
            </TableFooter>
          </Table>
        </TableContainer>
      </Paper>

      <Dialog open={dialogOpen} onClose={() => !saving && setDialogOpen(false)} fullWidth maxWidth="xl">
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
            <Typography variant="h5" fontWeight={700}>{pageTitle}</Typography>
            <Button size="small" onClick={() => setDialogOpen(false)}>{t('combo.listTitle')}</Button>
          </Stack>
        </DialogTitle>
        <DialogContent dividers>
          <Stack spacing={3}>
            <ComboStepper step={step} />
            {step === 0 ? (
              <GeneralStep form={form} errors={formErrors} options={options} updateForm={updateForm} />
            ) : (
              <ComboItemsStep form={form} errors={formErrors} setForm={setForm} />
            )}
          </Stack>
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'space-between', px: 3, py: 2 }}>
          <Box>
            {step === 1 ? (
              <Button startIcon={<ArrowLeft2 size={16} />} onClick={() => setStep(0)} disabled={saving}>{t('action.back')}</Button>
            ) : null}
          </Box>
          <Stack direction="row" spacing={1}>
            <Button color="inherit" onClick={() => setForm(editingId ? form : emptyForm())} disabled={saving}>{t('action.reset')}</Button>
            {step === 0 ? (
              <Button variant="contained" endIcon={<ArrowRight2 size={16} />} onClick={goNext}>{t('action.next')}</Button>
            ) : (
              <>
                <Button variant="outlined" startIcon={<Save2 size={16} />} onClick={() => void save(false)} disabled={saving}>{t('action.saveStay')}</Button>
                <Button variant="contained" startIcon={<Save2 size={16} />} onClick={() => void save(true)} disabled={saving}>{t('action.submit')}</Button>
              </>
            )}
          </Stack>
        </DialogActions>
      </Dialog>

      <Dialog open={detailOpen} onClose={() => setDetailOpen(false)} fullWidth maxWidth="md">
        <DialogTitle>{form.name || t('combo.singular')}</DialogTitle>
        <DialogContent dividers>
          <Stack spacing={2}>
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, md: 4 }}><Info label={tLabel('Code')} value={form.code} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><Info label={tLabel('Category')} value={selectOptionLabel(options.categories, form.category_id)} /></Grid>
              <Grid size={{ xs: 12, md: 4 }}><Info label={tLabel('Price')} value={moneyValue(normalizePayload(form).price)} /></Grid>
            </Grid>
            <ComboItemsReadOnly portions={form.portions} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailOpen(false)}>{t('action.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(deleteTarget)} onClose={() => setDeleteTarget(null)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('combo.deleteTitle')}</DialogTitle>
        <DialogContent>
          <Typography>{t('combo.deleteMessage', { name: String(deleteTarget?.name ?? t('combo.thisCombo')) })}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteTarget(null)}>{t('action.cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => void remove()}>{t('action.delete')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={Boolean(labelRow)} onClose={() => setLabelRow(null)} maxWidth="sm" fullWidth>
        <DialogTitle>{t('catalog.label.title', { name: String(labelRow?.name ?? '') })}</DialogTitle>
        <DialogContent dividers>{labelRow ? <ItemLabelPreview row={labelRow} /> : null}</DialogContent>
        <DialogActions><Button onClick={() => setLabelRow(null)}>{t('action.close')}</Button></DialogActions>
      </Dialog>

      <Dialog open={bulkOpen} onClose={() => setBulkOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>{t('action.deleteSelected')}</DialogTitle>
        <DialogContent><Typography>{t('crud.bulkDeleteMessage', { count: selected.size })}</Typography></DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkOpen(false)}>{t('action.cancel')}</Button>
          <Button color="error" variant="contained" onClick={() => void removeSelected()}>{t('action.delete')}</Button>
        </DialogActions>
      </Dialog>

      {toast ? (
        <Snackbar open autoHideDuration={5000} onClose={() => setToast(null)}>
          <Alert severity={toast.severity} onClose={() => setToast(null)}>{toast.message}</Alert>
        </Snackbar>
      ) : null}
    </Stack>
  );
}

function ComboStepper({ step }: { step: number }) {
  const items = [
    { title: t('combo.generalStep'), subtitle: t('combo.generalDetails') },
    { title: t('combo.itemsStep'), subtitle: t('combo.setComboItems') }
  ];
  return (
    <Stack direction="row" spacing={2} alignItems="center">
      {items.map((item, index) => (
        <Stack key={item.title} direction="row" spacing={1.25} alignItems="center" sx={{ flex: 1, opacity: index <= step ? 1 : 0.55 }}>
          <Chip label={index + 1} color={index <= step ? 'primary' : 'default'} />
          <Box sx={{ minWidth: 150 }}>
            <Typography fontWeight={700}>{item.title}</Typography>
            <Typography variant="caption" color="text.secondary">{item.subtitle}</Typography>
          </Box>
          {index === 0 ? <Divider sx={{ flex: 1 }} /> : null}
        </Stack>
      ))}
    </Stack>
  );
}

function GeneralStep({
  form,
  errors,
  options,
  updateForm
}: {
  form: ComboForm;
  errors: ValidationErrors;
  options: { categories: SelectOption[]; taxes: SelectOption[]; modifiers: SelectOption[] };
  updateForm: <K extends keyof ComboForm>(key: K, value: ComboForm[K]) => void;
}) {
  return (
    <Grid container spacing={3}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={2}>
          <TextField label={tLabel('Name')} required value={form.name} onChange={(event) => updateForm('name', event.target.value)} error={Boolean(fieldError(errors, 'name'))} helperText={fieldError(errors, 'name')} />
          <TextField label={tLabel('Code')} required value={form.code} onChange={(event) => updateForm('code', event.target.value)} error={Boolean(fieldError(errors, 'code'))} helperText={fieldError(errors, 'code') || t('combo.codeHelper')} />
          <TextField select label={t('combo.symbology')} required value={form.symbology} onChange={(event) => updateForm('symbology', event.target.value)} error={Boolean(fieldError(errors, 'symbology'))} helperText={fieldError(errors, 'symbology')}>
            {symbologyOptions.map((option) => <MenuItem key={option} value={option}>{option}</MenuItem>)}
          </TextField>
          <Autocomplete
            options={options.categories}
            value={options.categories.find((option) => String(option.value) === String(form.category_id)) ?? null}
            onChange={(_event, value) => updateForm('category_id', value?.value ?? '')}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label={tLabel('Category')} required error={Boolean(fieldError(errors, 'category_id'))} helperText={fieldError(errors, 'category_id')} />}
          />
          <Autocomplete
            multiple
            options={options.taxes}
            value={options.taxes.filter((option) => form.taxes.map(String).includes(String(option.value)))}
            onChange={(_event, value) => updateForm('taxes', value.map((option) => option.value))}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label={t('nav.taxes')} />}
          />
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography sx={{ minWidth: 90 }} color="text.secondary">{t('combo.photo')}</Typography>
            <Button variant="outlined" component="label">
              {form.photo ? form.photo.name : t('combo.selectPhoto')}
              <input hidden type="file" accept="image/*" onChange={(event) => updateForm('photo', event.target.files?.[0] ?? null)} />
            </Button>
          </Stack>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} alignItems={{ xs: 'stretch', sm: 'center' }}>
            <Typography sx={{ minWidth: 90 }} color="text.secondary">{t('combo.galleryImages')}</Typography>
            <Button variant="outlined" component="label">
              {form.images?.length ? t('combo.filesSelected', { count: form.images.length }) : t('combo.selectGalleryImages')}
              <input hidden multiple type="file" accept="image/*" onChange={(event) => updateForm('images', Array.from(event.target.files ?? []))} />
            </Button>
          </Stack>
        </Stack>
      </Grid>
      <Grid size={{ xs: 12, md: 6 }}>
        <Stack spacing={2}>
          <TextField label={t('combo.sku')} value={form.sku} InputProps={{ readOnly: true }} helperText={t('combo.generatedAutomatically')} />
          <TextField label={t('combo.altName')} value={form.alt_name} onChange={(event) => updateForm('alt_name', event.target.value)} />
          <TextField label={t('combo.hsnNumber')} value={form.hsn_number} onChange={(event) => updateForm('hsn_number', event.target.value)} />
          <TextField label={t('combo.maxDiscount')} type="number" value={form.max_discount} onChange={(event) => updateForm('max_discount', event.target.value)} helperText={t('combo.maxDiscountHelper')} />
          <Autocomplete
            multiple
            options={options.modifiers}
            value={options.modifiers.filter((option) => form.modifiers.map(String).includes(String(option.value)))}
            onChange={(_event, value) => updateForm('modifiers', value.map((option) => option.value))}
            isOptionEqualToValue={(option, value) => option.value === value.value}
            getOptionLabel={(option) => option.label}
            renderInput={(params) => <TextField {...params} label={t('nav.modifiers')} />}
          />
        </Stack>
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth label={t('combo.videoUrl')} value={form.video} onChange={(event) => updateForm('video', event.target.value)} helperText={t('combo.videoHelper')} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth label={tLabel('Summary')} multiline minRows={2} value={form.summary} onChange={(event) => updateForm('summary', event.target.value)} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <TextField fullWidth label={tLabel('Details')} multiline minRows={3} value={form.details} onChange={(event) => updateForm('details', event.target.value)} />
      </Grid>
      <Grid size={{ xs: 12 }}>
        <Stack direction="row" spacing={3}>
          <FormControlLabel control={<Checkbox checked={form.tax_included} onChange={(event) => updateForm('tax_included', event.target.checked)} />} label={t('combo.taxIncluded')} />
          <FormControlLabel control={<Checkbox checked={form.hide_in_pos} onChange={(event) => updateForm('hide_in_pos', event.target.checked)} />} label={t('combo.hideInPos')} />
        </Stack>
      </Grid>
    </Grid>
  );
}

function ComboItemsStep({ form, errors, setForm }: { form: ComboForm; errors: ValidationErrors; setForm: React.Dispatch<React.SetStateAction<ComboForm>> }) {
  const updatePortion = (portionIndex: number, updater: (portion: ComboPortion) => ComboPortion) => {
    setForm((current) => ({
      ...current,
      portions: current.portions.map((portion, index) => (index === portionIndex ? updater(portion) : portion))
    }));
  };

  const addPortion = () => {
    setForm((current) => ({ ...current, portions: [...current.portions, emptyPortion(`portion ${current.portions.length + 1}`)] }));
  };

  const addGroupToAll = () => {
    const groupName = window.prompt(t('combo.groupTypeName'));
    if (!groupName?.trim()) return;
    setForm((current) => ({
      ...current,
      portions: current.portions.map((portion) => ({
        ...portion,
        choosables: [...portion.choosables, { rowKey: rowKey('group'), name: groupName.trim(), items: [emptyLine()] }]
      }))
    }));
  };

  return (
    <Stack spacing={2}>
      {fieldError(errors, 'portions') ? <Alert severity="error">{fieldError(errors, 'portions')}</Alert> : null}
      <Stack direction="row" justifyContent="flex-end">
        <Button startIcon={<Add size={16} />} variant="contained" color="success" onClick={addPortion}>{t('combo.addPortion')}</Button>
      </Stack>
      <Grid container spacing={2}>
        {form.portions.map((portion, index) => (
          <Grid size={{ xs: 12, lg: 6 }} key={portion.rowKey}>
            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
              <Stack spacing={2}>
                <Stack direction="row" spacing={1} alignItems="center">
                  <TextField label={tLabel('Portions')} value={portion.name} onChange={(event) => updatePortion(index, (current) => ({ ...current, name: event.target.value }))} sx={{ flex: 1 }} />
                  {form.portions.length > 1 ? (
                    <Tooltip title={t('combo.removePortion')}>
                      <IconButton color="error" onClick={() => setForm((current) => ({ ...current, portions: current.portions.filter((_, i) => i !== index) }))}><Trash size={18} /></IconButton>
                    </Tooltip>
                  ) : null}
                </Stack>
                <ComboLineSection
                  title={t('combo.essentialItems')}
                  lines={portion.essentials}
                  onChange={(lines) => updatePortion(index, (current) => ({ ...current, essentials: lines }))}
                />
                <Stack spacing={1}>
                  <Stack direction="row" justifyContent="space-between" alignItems="center">
                    <Typography variant="h6" fontWeight={700}>{t('combo.choosableItems')}</Typography>
                    <Button size="small" startIcon={<Add size={15} />} onClick={addGroupToAll}>{t('combo.addGroupType')}</Button>
                  </Stack>
                  {portion.choosables.length ? portion.choosables.map((group, groupIndex) => (
                    <Paper key={group.rowKey} variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                      <Stack spacing={1.5}>
                        <Stack direction="row" spacing={1} alignItems="center">
                          <TextField label={t('combo.groupType')} value={group.name} onChange={(event) => updatePortion(index, (current) => ({ ...current, choosables: current.choosables.map((g, i) => (i === groupIndex ? { ...g, name: event.target.value } : g)) }))} sx={{ flex: 1 }} />
                          <Tooltip title={t('combo.removeGroup')}>
                            <IconButton color="error" onClick={() => updatePortion(index, (current) => ({ ...current, choosables: current.choosables.filter((_, i) => i !== groupIndex) }))}><CloseCircle size={18} /></IconButton>
                          </Tooltip>
                        </Stack>
                        <ComboLineSection
                          title={t('combo.choices')}
                          compact
                          lines={group.items}
                          onChange={(lines) => updatePortion(index, (current) => ({ ...current, choosables: current.choosables.map((g, i) => (i === groupIndex ? { ...g, items: lines } : g)) }))}
                        />
                      </Stack>
                    </Paper>
                  )) : (
                    <Typography variant="body2" color="text.secondary">{t('combo.noChoosableGroups')}</Typography>
                  )}
                </Stack>
                <Grid container spacing={2}>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label={tLabel('Cost')} type="number" value={portion.cost} onChange={(event) => updatePortion(index, (current) => ({ ...current, cost: event.target.value }))} helperText={fieldError(errors, `portions.${index}.cost`) || t('combo.costHelper', { amount: portionMinCost(portion).toFixed(2) })} />
                  </Grid>
                  <Grid size={{ xs: 12, sm: 6 }}>
                    <TextField fullWidth label={tLabel('Price')} type="number" value={portion.price} onChange={(event) => updatePortion(index, (current) => ({ ...current, price: event.target.value }))} error={Boolean(fieldError(errors, `portions.${index}.price`))} helperText={fieldError(errors, `portions.${index}.price`) || t('combo.priceHelper')} />
                  </Grid>
                </Grid>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Stack>
  );
}

function ComboLineSection({ title, lines, onChange, compact = false }: { title: string; lines: ComboLine[]; onChange: (lines: ComboLine[]) => void; compact?: boolean }) {
  const updateLine = (index: number, patch: Partial<ComboLine>) => {
    onChange(lines.map((line, i) => (i === index ? { ...line, ...patch } : line)));
  };

  return (
    <Paper variant="outlined" sx={{ borderRadius: 2, overflow: 'hidden' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ px: 2, py: 1.5 }}>
        <Typography variant={compact ? 'subtitle1' : 'h6'} fontWeight={700}>{title}</Typography>
        <Button size="small" startIcon={<Add size={15} />} onClick={() => onChange([...lines, emptyLine()])}>{t('combo.addItem')}</Button>
      </Stack>
      <Divider />
      <Table size="small">
        <TableHead>
          <TableRow>
            <TableCell>{tLabel('Name')}</TableCell>
            <TableCell>{t('combo.variation')}</TableCell>
            <TableCell width={130}>{tLabel('Quantity')}</TableCell>
            <TableCell width={48} />
          </TableRow>
        </TableHead>
        <TableBody>
          {lines.map((line, index) => (
            <TableRow key={line.rowKey}>
              <TableCell>
                <ItemRemoteAutocomplete
                  line={line}
                  onChange={(item) =>
                    updateLine(index, {
                      id: item?.id,
                      item_id: item?.id,
                      name: item?.name ?? '',
                      code: item?.code ?? '',
                      cost: item?.cost ?? 0,
                      variations: item?.variations ?? [],
                      variation_id: null
                    })
                  }
                />
              </TableCell>
              <TableCell>
                <TextField select fullWidth size="small" value={line.variation_id ?? ''} onChange={(event) => updateLine(index, { variation_id: event.target.value || null })} disabled={!line.variations?.length}>
                  <MenuItem value="">{t('common.none')}</MenuItem>
                  {(line.variations ?? []).map((variation) => (
                    <MenuItem key={String(variation.id)} value={String(variation.id)}>{variationLabel(variation)}</MenuItem>
                  ))}
                </TextField>
              </TableCell>
              <TableCell>
                <TextField size="small" type="number" inputProps={{ min: 0, step: 1 }} value={line.quantity} onChange={(event) => updateLine(index, { quantity: Number(event.target.value) })} />
              </TableCell>
              <TableCell align="right">
                <Tooltip title={t('combo.removeItem')}>
                  <IconButton color="error" size="small" onClick={() => onChange(lines.filter((_, i) => i !== index))}><Trash size={16} /></IconButton>
                </Tooltip>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Paper>
  );
}

function ItemRemoteAutocomplete({ line, onChange }: { line: ComboLine; onChange: (item: ItemOption | null) => void }) {
  const [inputValue, setInputValue] = useState('');
  const [options, setOptions] = useState<ItemOption[]>([]);
  const selected = useMemo<ItemOption | null>(() => {
    if (!line.item_id) return null;
    return {
      id: line.item_id,
      name: line.name ?? '',
      code: line.code,
      cost: line.cost,
      label: line.name && line.code ? `${line.name} (${line.code})` : line.name,
      variations: line.variations ?? []
    };
  }, [line.code, line.cost, line.item_id, line.name, line.variations]);

  useEffect(() => {
    let cancelled = false;
    const timer = window.setTimeout(() => {
      adminApi
        .get<ItemOption[]>('items/search', { params: { q: inputValue, with: 'variations', cost: 'yes', type: 'standard,service,recipe' } })
        .then((response) => {
          if (!cancelled) setOptions(response.data ?? []);
        })
        .catch(() => {
          if (!cancelled) setOptions([]);
        });
    }, 180);
    return () => {
      cancelled = true;
      window.clearTimeout(timer);
    };
  }, [inputValue]);

  return (
    <Autocomplete
      size="small"
      options={options}
      value={selected}
      onInputChange={(_event, value, reason) => {
        if (reason !== 'reset') setInputValue(value);
      }}
      onChange={(_event, value) => {
        setInputValue('');
        onChange(value);
      }}
      isOptionEqualToValue={(option, value) => String(option.id) === String(value.id)}
      getOptionLabel={(option) => option.label ?? `${option.name}${option.code ? ` (${option.code})` : ''}`}
      renderInput={(params) => <TextField {...params} placeholder={t('combo.searchItemPlaceholder')} />}
    />
  );
}

function portionMinCost(portion: ComboPortion) {
  const essentials = portion.essentials.reduce((sum, line) => sum + Number(line.cost ?? 0) * Number(line.quantity || 0), 0);
  const choices = portion.choosables.reduce((sum, group) => {
    const max = group.items.reduce((best, line) => Math.max(best, Number(line.cost ?? 0) * Number(line.quantity || 0)), 0);
    return sum + max;
  }, 0);
  return essentials + choices;
}

function Info({ label, value }: { label: string; value: string }) {
  return (
    <Box>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography fontWeight={700}>{value || '-'}</Typography>
    </Box>
  );
}

function ComboItemsReadOnly({ portions }: { portions: ComboPortion[] }) {
  return (
    <Stack spacing={2}>
      {portions.map((portion) => (
        <Paper key={portion.rowKey} variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Stack spacing={1}>
            <Typography variant="h6" fontWeight={700}>{portion.name}</Typography>
            <Typography variant="subtitle2">{t('combo.essentialItems')}</Typography>
            {portion.essentials.filter((line) => line.item_id).map((line) => (
              <Typography key={line.rowKey} variant="body2">{line.name} x {line.quantity}</Typography>
            ))}
            {portion.choosables.length ? (
              <>
                <Typography variant="subtitle2">{t('combo.choosableItems')}</Typography>
                {portion.choosables.map((group) => (
                  <Typography key={group.rowKey} variant="body2">{group.name}: {group.items.map((line) => line.name).filter(Boolean).join(', ') || '-'}</Typography>
                ))}
              </>
            ) : null}
          </Stack>
        </Paper>
      ))}
    </Stack>
  );
}
